import type { Nominal } from '../../../shared/lib/typeHelpers.no-deps'

export type SD_TLegacyTarget = Array<{ id: number; target: 'units' | 'locations' }>

export type SD_TTarget = { location: number } | { unit: number } | { location: number; unit: number }

export type SD_TReferenceId = Nominal<string, 'smart-data-reference'>

export type SD_TReference = {
    reference: SD_TReferenceId
    ids: Array<number | string>
}

type SmartData = {
    id: number
    targets: SD_TLegacyTarget[]
}

function cmp(a: any, b: any) {
    if (a === b) return 0
    if (a < b) return -1
    return 1
}

function sortByTypeThenId(a: SD_TLegacyTarget[0], b: SD_TLegacyTarget[0]) {
    if (a.target === b.target) {
        return cmp(a.id, b.id)
    }

    return cmp(a.target, b.target)
}

export function SD_legacyTargetToReferenceId(target: SD_TLegacyTarget) {
    const sortedTarget = [...target].sort(sortByTypeThenId)
    const targetArray = sortedTarget.map((t) => [t.target, t.id])
    return JSON.stringify(targetArray) as SD_TReferenceId
}

function isLocationTarget(target: SD_TTarget): target is { location: number } {
    return (target as any).location && !(target as any).unit
}

function isUnitTarget(target: SD_TTarget): target is { unit: number } {
    return !(target as any).location && (target as any).unit
}

function isUnitLocationTarget(target: SD_TTarget): target is { location: number; unit: number } {
    return (target as any).location && (target as any).unit
}

export function SD_toReferenceId(target: SD_TTarget): SD_TReferenceId {
    if (isUnitLocationTarget(target)) {
        return `[["locations",${target.location}],["units",${target.unit}]]` as SD_TReferenceId
    } else if (isUnitTarget(target)) {
        return `[["units",${target.unit}]]` as SD_TReferenceId
    } else if (isLocationTarget(target)) {
        return `[["locations",${target.location}]]` as SD_TReferenceId
    }
}

export function SD_toLegacyTarget(target: SD_TTarget): SD_TLegacyTarget {
    if (isUnitLocationTarget(target)) {
        return [
            { target: 'units', id: target.unit },
            { target: 'locations', id: target.location },
        ]
    }
    if (isLocationTarget(target)) return [{ target: 'locations', id: target.location }]
    if (isUnitTarget(target)) return [{ target: 'units', id: target.unit }]
}

export function SD_extractTargetReferences(smartData: SmartData[], references: SD_TReference[]) {
    const referenceMap = new Map<SD_TReferenceId, Array<string | number>>(
        references.map((ref): [SD_TReferenceId, Array<string | number>] => [ref.reference, ref.ids])
    )

    smartData.forEach((smd) => {
        smd.targets.forEach((target) => {
            const referenceId = SD_legacyTargetToReferenceId(target)

            if (referenceMap.has(referenceId)) {
                const ids = referenceMap.get(referenceId)
                referenceMap.set(referenceId, [...ids, smd.id])
            } else {
                referenceMap.set(referenceId, [smd.id])
            }
        })
    })

    const newReferences: SD_TReference[] = []

    referenceMap.forEach((ids, referenceId) => newReferences.push({ reference: referenceId, ids }))

    return newReferences
}
